import {Action, Selector, State, StateContext, Store} from '@ngxs/store';
import {tap} from 'rxjs/operators';
import {Localization, LocalizationIdentity} from '../../models/localization.model';
import {FormState} from '../../models/form-state.model';
import {SearchList} from '../../models/search-list.model';
import {NavService} from '../../services/nav.service';
import {LocalizationService} from '../../services/localization.service';
import {PartnerState} from '../partner/partner.state';
import {Injectable} from '@angular/core';
import {setFormStatus, setFormValidationErrors} from '../../helpers/form.helper';
import {handleFetchListPage} from '../../helpers/search.helper';
import {FormStatus} from '../../enums/file-status.enum';
import {NavItemCodes} from '../../enums/nav-item-codes.enum';
import {LocalizationActions} from './localization.actions';

interface LocalizationStateModel {
    form: FormState;
    list: SearchList<Localization>;
    activeLocalization: Localization;
    filterList: LocalizationIdentity[];
}

const STATE_NAME = 'localization';

@State<LocalizationStateModel>({
    name: STATE_NAME,
    defaults: {
        form: new FormState(),
        list: new SearchList<Localization>({
            filters: new FormState()
        }),
        activeLocalization: null,
        filterList: []
    }
})

@Injectable()
export class LocalizationState {
    constructor(
        private store: Store,
        private navService: NavService,
        private localizationService: LocalizationService
    ) {}

    @Selector()
    static localizationList(state) {
        return state.list;
    }

    @Selector()
    static activeLocalization(state) {
        return state.activeLocalization;
    }

    @Selector()
    static localizationFormErrors(state) {
        return state.form.validationErrors;
    }

    @Selector()
    static localizationFilterList(state) {
        return state.filterList;
    }

    @Action(LocalizationActions.GetListPage)
    getListPage(ctx: StateContext<LocalizationStateModel>, action: LocalizationActions.GetListPage) {
        const partner = this.store.selectSnapshot(PartnerState.activePartner);
        return handleFetchListPage(ctx, action.params, props => this.localizationService.fetchList(partner.id, props));
    }

    @Action(LocalizationActions.Activate)
    activateLocalization(
        ctx: StateContext<LocalizationStateModel>,
        {partnerId, localizationId}: LocalizationActions.Activate
    ) {
        return this.localizationService.activate(partnerId, localizationId).subscribe(
            () => ctx.dispatch(new LocalizationActions.GetListPage())
        );
    }

    @Action(LocalizationActions.Deactivate)
    deactivateLocalization(
        ctx: StateContext<LocalizationStateModel>,
        {partnerId, localizationId}: LocalizationActions.Deactivate
    ) {
        return this.localizationService.deactivate(partnerId, localizationId).subscribe(
            () => ctx.dispatch(new LocalizationActions.GetListPage())
        );
    }

    @Action(LocalizationActions.Add)
    addLocalization(ctx: StateContext<LocalizationStateModel>, {localization}: LocalizationActions.Add) {
        const partner = this.store.selectSnapshot(PartnerState.activePartner);
        setFormStatus(ctx, FormStatus.PENDING, STATE_NAME);
        return this.localizationService.add(partner.id, localization).subscribe(
            () => this.navService.navigateByCode(NavItemCodes.LOCALIZATION_MANAGEMENT, {partnerId: partner.id}),
            ({error}) => ctx.dispatch(new LocalizationActions.FormFailure(error))
        );
    }

    @Action(LocalizationActions.Update)
    updateLocalization(ctx: StateContext<LocalizationStateModel>, {localization}: LocalizationActions.Update) {
        setFormStatus(ctx, FormStatus.PENDING, STATE_NAME);
        return this.localizationService.update(localization).subscribe(
            () => this.navService.navigateByCode(NavItemCodes.LOCALIZATION_MANAGEMENT, {partnerId: localization.partner.id}),
            ({error}) => ctx.dispatch(new LocalizationActions.FormFailure(error))
        );
    }

    @Action(LocalizationActions.FormFailure)
    addLocalizationFailure(
        ctx: StateContext<LocalizationStateModel>,
        {validationErrors = []}: LocalizationActions.FormFailure
    ) {
        setFormValidationErrors(ctx, validationErrors, STATE_NAME);
    }

    @Action(LocalizationActions.GetById)
    getLocalization(ctx: StateContext<LocalizationStateModel>, {partnerId, localizationId}: LocalizationActions.GetById) {
        return this.localizationService.fetchById(partnerId, localizationId).pipe(
            tap((localization) => {
                ctx.patchState({
                    activeLocalization: new Localization(localization)
                });
            })
        );
    }

    @Action(LocalizationActions.FilterList)
    getFilterList(ctx: StateContext<LocalizationStateModel>, {partnerId}: LocalizationActions.FilterList) {
        return this.localizationService.fetchFilterList(partnerId).pipe(
            tap((items: LocalizationIdentity[]) => {
                ctx.patchState({
                    filterList: items
                });
            })
        );
    }

    @Action(LocalizationActions.Clear)
    localizationClear(ctx: StateContext<LocalizationStateModel>) {
        ctx.patchState({
            activeLocalization: null
        });
    }

    @Action(LocalizationActions.ListClear)
    localizationListClear(ctx: StateContext<LocalizationStateModel>) {
        ctx.patchState({
            list: new SearchList<Localization>({
                filters: new FormState(),
            }),
        });
    }
}
